import { useMemo } from 'react';
import type { Product, ProductPosition } from '@/api/products';
import {
  transformProduct,
  type UIProductMockupTemplate,
} from '@/lib/product-transformer';
import { buildPrintContextParamsFromTemplate } from '@/lib/printContextBuilder';
import {
  useProductSelectionStore,
  type MockupPosition,
} from '@/stores/productSelectionStore';
import { useCanvasStateManager } from '@/stores/canvasStateManager';

export interface DirectSalePositionEntry {
  position: ProductPosition;
  template: UIProductMockupTemplate;
}

/**
 * The positions a customer can switch between, and the switch itself —
 * shared by the desktop pills and the phone's view switcher.
 *
 * An entry exists only for positions that are sellable (carry a price) AND
 * have a mockup template — a priced position without a template would
 * render the wrong mockup while collecting billable artwork.
 *
 * Switching goes through productSelectionStore.setActivePosition with the
 * template's print context, exactly like the Seller BottomBar — calling
 * canvasStateManager.switchPosition directly would leave the selection store
 * stale and fight ProductPreview's auto-correction effect. The undo history
 * is cleared on switch: undo() restores onto the CURRENT canvas ignoring
 * which position an entry belonged to, so a stale stack would deserialize
 * the previous position's design onto the new one.
 */
export function useDirectSalePositions(
  product: Product,
  sellablePositions: ProductPosition[]
) {
  const activePosition = useProductSelectionStore((s) => s.activePosition);
  const setActivePosition = useProductSelectionStore(
    (s) => s.setActivePosition
  );
  const isSwitching = useCanvasStateManager((s) => s.isSwitchingPosition);

  /** Sellable ∩ has-template, in the product's own position order. */
  const entries = useMemo<DirectSalePositionEntry[]>(() => {
    const templatesByPosition = new Map<string, UIProductMockupTemplate>();
    for (const template of transformProduct(product).mockupTemplates) {
      const existing = templatesByPosition.get(template.positionKey);
      if (!existing || template.isDefault) {
        templatesByPosition.set(template.positionKey, template);
      }
    }

    return sellablePositions.flatMap((position) => {
      const template = templatesByPosition.get(position.slug);
      return template ? [{ position, template }] : [];
    });
  }, [product, sellablePositions]);

  const switchTo = ({ position, template }: DirectSalePositionEntry) => {
    if (isSwitching || position.slug === activePosition) return;

    setActivePosition(
      position.slug as MockupPosition,
      position.id,
      buildPrintContextParamsFromTemplate(template)
    );
    // Cross-store call — a history failure must never block the switch
    // (cf. docs/solutions/ui-bugs/wrong-product-mockup-edit-placement.md).
    try {
      useCanvasStateManager.getState().clearHistory();
    } catch {
      // Stale history survives; the switch itself already happened.
    }
  };

  return { entries, activePosition, isSwitching, switchTo };
}
